import { AdKitKey } from "types/enums";
import type { PlacementCatalogEntry } from "../placementCatalog";
import {
  placementInstances,
  type CampaignDraft,
} from "../components/setup/campaignDraft";
import type { CampaignEdit } from "../components/setup/campaignEdits";
import type { AdSpacesProps } from "./trackAdSpaces";

/** "HOLE_LOAD_AD", not 2: Pendo reports read better by name. */
export const placementName = (key: AdKitKey): string =>
  AdKitKey[key] ?? String(key);

const joinSorted = (values: Iterable<string>) => [...values].sort().join(",");

/**
 * The choices made in setup, for Create Campaign. Counts and enum names only:
 * no campaign name, description or image ids.
 */
export function createCampaignProps(
  draft: CampaignDraft,
  catalog: Pick<
    PlacementCatalogEntry,
    "key" | "isHoleEnabled" | "hasTransitions"
  >[],
): AdSpacesProps {
  const byKey = new Map(catalog.map((p) => [p.key, p]));
  const holeModes = new Set<string>();
  let imageCount = 0;
  let sponsorPlacementCount = 0;
  let hasTimedPlacement = false;

  for (const key of draft.placements) {
    const setup = draft.setup[key];
    const placement = byKey.get(key);
    if (!setup || !placement) continue;
    imageCount += placementInstances(setup).length;
    if (setup.imageMode === "sponsors") sponsorPlacementCount += 1;
    if (placement.isHoleEnabled) holeModes.add(setup.holeMode);
    hasTimedPlacement ||= placement.hasTransitions;
  }

  return {
    source: draft.source.kind,
    placements: joinSorted(draft.placements.map(placementName)),
    placementCount: draft.placements.length,
    imageCount,
    sponsorPlacementCount,
    holeModes: joinSorted(holeModes),
    // Only meaningful when Hole Load is in the campaign.
    holeLoadTiming: hasTimedPlacement
      ? draft.timing
        ? "custom"
        : "default"
      : undefined,
    hasDescription: draft.description.trim() !== "",
    simulatorCount: draft.simulatorIds.length,
  };
}

/** What an edit save changed, for Edit Campaign. */
export function editCampaignProps(edits: CampaignEdit[]): AdSpacesProps {
  const count = (kind: CampaignEdit["kind"]) =>
    edits.filter((e) => e.kind === kind).length;
  let placementsAdded = 0;
  let imagesAdded = 0;
  const placements = new Set<string>();

  for (const edit of edits) {
    if (edit.kind === "addLocations") {
      placementsAdded += edit.body.length;
      for (const location of edit.body) {
        placements.add(placementName(location.adKitMetaDataId));
      }
    } else if (edit.kind !== "updateTiming") {
      placements.add(placementName(edit.key));
    }
    if (edit.kind === "addInstances") imagesAdded += edit.body.length;
  }

  return {
    placementsChanged: joinSorted(placements),
    placementsAdded,
    placementsRemoved: count("deleteLocation"),
    holesChanged: count("updateHoles"),
    imagesAdded,
    imagesRemoved: count("deleteInstance"),
    imagesReordered: count("reorderInstances"),
    timingChanged: count("updateTiming") > 0,
  };
}
